import type { ReactNode } from 'react'

import { useEffect, useId } from 'react'

import { styled } from '../core'
import { selectionControlColors as colors, selectionControlDisabled as disabledColors } from './consts'

// Visually hidden native input stacked over the control icon — identical positioning for Checkbox and Radio.
export const SelectionControlInput = styled.input`
  cursor: inherit;
  height: 100%;
  left: 0;
  margin: 0;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`

/** Warns once per size change when the deprecated `lg` size alias is used. */
export function useDeprecatedLgSize(size: string | undefined, componentName: string): void {
  useEffect(() => {
    if (size === 'lg') {
      console.warn(`[${componentName}] size='lg' is deprecated and matches 'md'. Use size='md' instead.`)
    }
  }, [size, componentName])
}

/** Resolves the `id`/description pairing shared by Checkbox and Radio's `helpText`/`errorText` sockets. */
export function useSelectionControlDescription(
  idProp: string | undefined,
  helpText?: ReactNode,
  errorText?: ReactNode,
  isActive?: boolean,
) {
  const generatedId = useId()
  const id = idProp ?? generatedId
  // Checking/selecting the control clears its error state — errorText only applies while unselected.
  const effectiveErrorText = isActive ? undefined : errorText
  const description = effectiveErrorText ?? helpText
  const descriptionId = description ? `${id}-description` : undefined
  return { description, descriptionId, errorText: effectiveErrorText, id }
}

/** Priority for a selection control's border/fill colour: disabled, then error state, then checked/readOnly, then default. */
function resolveControlColor(args: {
  checkedColor: string
  controlColor: string
  disabled?: boolean
  disabledColor: string
  errorColor: string
  errorText?: ReactNode
  isActive: boolean
  readOnly?: boolean
  readOnlyColor: string
}): string {
  const {
    checkedColor,
    controlColor,
    disabled,
    disabledColor,
    errorColor,
    errorText,
    isActive,
    readOnly,
    readOnlyColor,
  } = args
  if (disabled) return disabledColor
  if (errorText) return errorColor
  if (isActive) return readOnly ? readOnlyColor : checkedColor
  return controlColor
}

/** Priority for a selection control's label colour: disabled greys it out, otherwise the default label colour. */
function resolveLabelColor(args: { disabled?: boolean; disabledColor: string; labelColor: string }): string {
  const { disabled, disabledColor, labelColor } = args
  return disabled ? disabledColor : labelColor
}

/** Priority for a selection control's helper/error description colour: disabled, then error state, then helper. */
function resolveDescriptionColor(args: {
  disabled?: boolean
  disabledColor: string
  errorColor: string
  errorText?: ReactNode
  helperColor: string
}): string {
  const { disabled, disabledColor, errorColor, errorText, helperColor } = args
  if (disabled) return disabledColor
  if (errorText) return errorColor
  return helperColor
}

/**
 * Error state always wins (the theme's baseStyle bakes in a non-undefined hover/pressed default, so it can't
 * be treated as an opt-in override); otherwise explicit wins, then the active/inactive state colour.
 */
function resolveStateColor(args: {
  activeColor: string
  errorColor?: string
  errorText?: ReactNode
  explicit?: string
  inactiveColor?: string
  isActive: boolean
}): string {
  const { activeColor, errorColor, errorText, explicit, inactiveColor = activeColor, isActive } = args
  if (errorText && errorColor) return errorColor
  return explicit ?? (isActive ? activeColor : inactiveColor)
}

/**
 * Bundles every colour computation shared by Checkbox and Radio — control border/fill, hover/pressed state,
 * label and helper/error description — so both stay in lockstep with a single disabled/error/checked priority.
 */
export function useSelectionControlColors(args: {
  controlColor: string
  // Checkbox darkens the border on hover/pressed only while unchecked (Radio always uses the same hover/pressed colour).
  differentiateUnchecked?: boolean
  disabled?: boolean
  errorText?: ReactNode
  hoverColor?: string
  isActive: boolean
  pressedColor?: string
  readOnly?: boolean
}): {
  color: string
  controlHoverColor: string
  controlPressedColor: string
  descriptionColor: string
  labelColor: string
} {
  const { controlColor, differentiateUnchecked, disabled, errorText, hoverColor, isActive, pressedColor, readOnly } =
    args
  const inactiveStateColor = differentiateUnchecked ? colors.uncheckedHover : undefined

  const color = resolveControlColor({
    checkedColor: colors.checked,
    controlColor,
    disabled,
    disabledColor: disabledColors.icon,
    errorColor: colors.error,
    errorText,
    isActive,
    readOnly,
    readOnlyColor: colors.readOnly,
  })

  const controlHoverColor = resolveStateColor({
    activeColor: colors.hover,
    errorColor: colors.error,
    errorText,
    explicit: hoverColor,
    inactiveColor: inactiveStateColor,
    isActive,
  })

  const controlPressedColor = resolveStateColor({
    activeColor: colors.pressed,
    errorColor: colors.error,
    errorText,
    explicit: pressedColor,
    inactiveColor: inactiveStateColor,
    isActive,
  })

  const labelColor = resolveLabelColor({ disabled, disabledColor: disabledColors.text, labelColor: colors.label })

  const descriptionColor = resolveDescriptionColor({
    disabled,
    disabledColor: disabledColors.text,
    errorColor: colors.error,
    errorText,
    helperColor: colors.helper,
  })

  return { color, controlHoverColor, controlPressedColor, descriptionColor, labelColor }
}
